// ── usePullJob — async poll-until-ready hook for pull-from-remote jobs ──────
//
// Creates a pull job via the BFF, then polls GET /pull/:id until the server
// reports ready (manifest available) or failed/expired.  Uses react-query's
// `refetchInterval` with a dynamic callback so polling auto-stops on
// terminal states.
//
// Mirrors the "fire → poll → consume" pattern the platform already uses for
// large async operations (cf. WorkflowCatalogPanelV2 / IoTTelemetryPanel).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPullJob,
  getPullJob,
  listPullJobs,
  removePullJob,
  type PullJobCreated,
  type PullJobDetail,
  type PullJobParams,
  type PullJobStatus,
  type PullJobSummary,
} from '../../../api/pullPackageApi';
import type { ScopeManifest } from '../types';

// ── Constants ───────────────────────────────────────────────────────────────

/** Poll interval while the job is still running (2 s). */
const POLL_ACTIVE_MS = 2_000;

/** Poll interval when the job is close to ready (connecting → exporting). */
const POLL_FAST_MS = 1_000;

/** Terminal states that stop polling. */
const TERMINAL = new Set<PullJobStatus>(['ready', 'failed', 'expired']);

/**
 * Maximum client-side polling duration (6 min).
 * Slightly longer than the BFF worker's 5 min export timeout so the
 * server-side safety net fires first when possible.
 */
const MAX_POLL_DURATION_MS = 6 * 60 * 1_000;

/** Elapsed time update interval for the UI timer (1 s). */
const ELAPSED_TICK_MS = 1_000;

// ── Progress shape (matches BFF pull-job-store updateProgress) ──────────────

export interface PullJobProgress {
  phase?: string;
  total_frames?: number;
  frames_decoded?: number;
  scopes?: number;
  outcomes?: number;
  bytes_received?: number;
  target?: string;
  auth_method?: string;
  [key: string]: unknown;
}

// ── Hook ────────────────────────────────────────────────────────────────────

export interface UsePullJobReturn {
  /** Current active job ID (null when idle). */
  activeJobId: string | null;

  /** Full job detail (populated once polling receives data). */
  job: PullJobDetail | null;

  /** True while polling for an in-flight job. */
  isPolling: boolean;

  /** True while the POST /pull mutation is in-flight. */
  isCreating: boolean;

  /** Manifest extracted from a ready job (convenience accessor). */
  manifest: ScopeManifest | null;

  /** Human-readable error string (from job or network). */
  error: string | null;

  /** Structured progress from the BFF worker (phase, frames, bytes…). */
  progress: PullJobProgress | null;

  /** Milliseconds elapsed since the job was created. */
  elapsedMs: number;

  /** True when client-side polling timed out. */
  isTimedOut: boolean;

  /** Create a new pull job.  Replaces any previous active job. */
  create: (params: PullJobParams) => void;

  /** Cancel / remove the current job and reset state. */
  cancel: () => void;

  /** Clear result (manifest + error) without removing the job server-side. */
  clear: () => void;

  /** List of all known pull jobs (lightweight metadata, auto-refreshed). */
  jobs: PullJobSummary[];

  /** Whether the job list is loading. */
  jobsLoading: boolean;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Parse the polymorphic `progress` field into a typed object (or null). */
function parseProgress(raw: unknown): PullJobProgress | null {
  if (!raw) return null;
  if (typeof raw === 'object') return raw as PullJobProgress;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as PullJobProgress; } catch { return { phase: raw }; }
  }
  return null;
}

export function usePullJob(): UsePullJobReturn {
  const qc = useQueryClient();
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);

  // Ref to avoid stale closures in the refetchInterval callback.
  const jobStatusRef = useRef<PullJobStatus | null>(null);
  // Track when the job was created (client-side wall clock).
  const createdAtRef = useRef<number | null>(null);
  // Interval handle for the elapsed timer.
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Elapsed timer management ────────────────────────────────────────
  const startElapsedTimer = useCallback(() => {
    createdAtRef.current = Date.now();
    setElapsedMs(0);
    setIsTimedOut(false);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    elapsedTimerRef.current = setInterval(() => {
      const start = createdAtRef.current;
      if (!start) return;
      setElapsedMs(Date.now() - start);
    }, ELAPSED_TICK_MS);
  }, []);

  const stopElapsedTimer = useCallback(() => {
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, []);

  // Cleanup on unmount.
  useEffect(() => stopElapsedTimer, [stopElapsedTimer]);

  // ── Create mutation ─────────────────────────────────────────────────
  const createMut = useMutation<PullJobCreated, Error, PullJobParams>({
    mutationFn: createPullJob,
    onSuccess: (data) => {
      setActiveJobId(data.id);
      setError(null);
      setIsTimedOut(false);
      jobStatusRef.current = data.status;
      startElapsedTimer();
      // Invalidate list so the new job appears immediately.
      qc.invalidateQueries({ queryKey: ['pullJobs'] });
    },
    onError: (err) => {
      setError(err?.message ?? 'Failed to create pull job');
    },
  });

  // ── Poll query (enabled only when we have an active job) ────────────
  const pollQuery = useQuery<PullJobDetail>({
    queryKey: ['pullJob', activeJobId],
    queryFn: () => getPullJob(activeJobId!),
    enabled: !!activeJobId && !isTimedOut,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status || TERMINAL.has(status)) return false;

      // ── Client-side max-duration guard ──
      const start = createdAtRef.current;
      if (start && Date.now() - start > MAX_POLL_DURATION_MS) {
        return false; // stop polling — the effect below will set the timeout error
      }

      // Use faster polling when we're in early phases.
      return status === 'pending' ? POLL_FAST_MS : POLL_ACTIVE_MS;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
  });

  // ── Derived state ───────────────────────────────────────────────────
  const job = pollQuery.data ?? null;
  const manifest = job?.status === 'ready' ? (job.manifest ?? null) : null;
  const jobError = job?.status === 'failed' ? (job.error ?? 'Pull job failed') : null;
  const isPolling = !!activeJobId && !isTimedOut && !TERMINAL.has(job?.status as PullJobStatus);
  const progress = parseProgress(job?.progress);

  // Sync ref so refetchInterval callback always sees current status.
  if (job?.status) jobStatusRef.current = job.status;

  // ── Client-side timeout detection ───────────────────────────────────
  useEffect(() => {
    if (!activeJobId || isTimedOut) return;
    const start = createdAtRef.current;
    if (!start) return;
    const status = job?.status;
    if (status && TERMINAL.has(status)) return; // already terminal

    if (Date.now() - start > MAX_POLL_DURATION_MS) {
      setIsTimedOut(true);
      setError(
        `Pull job timed out after ${Math.round(MAX_POLL_DURATION_MS / 60_000)} min — ` +
        'the remote server may be unreachable or the export is too large. ' +
        'Cancel and retry, or check the server logs.',
      );
      stopElapsedTimer();
    }
  }, [activeJobId, isTimedOut, elapsedMs, job?.status, stopElapsedTimer]);

  // ── Stop timer on terminal state ────────────────────────────────────
  useEffect(() => {
    if (job?.status && TERMINAL.has(job.status)) {
      stopElapsedTimer();
    }
  }, [job?.status, stopElapsedTimer]);

  // Track whether the active job has reached a terminal state so we can
  // slow the job-list polling back down without fully clearing UI state.
  const isActiveTerminal = !!activeJobId && !!job?.status && TERMINAL.has(job.status);

  // ── Job list (lightweight, auto-refreshed) ──────────────────────────
  // Fast-poll only while the active job is still in-flight.  Once it
  // reaches a terminal state, fall back to the 30 s background cadence.
  const jobsQuery = useQuery<PullJobSummary[]>({
    queryKey: ['pullJobs'],
    queryFn: listPullJobs,
    refetchInterval: activeJobId && !isActiveTerminal ? POLL_ACTIVE_MS * 3 : 30_000,
    staleTime: 5_000,
  });

  // ── Remove mutation ─────────────────────────────────────────────────
  const removeMut = useMutation({
    mutationFn: (id: string) => removePullJob(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pullJobs'] }),
  });

  // ── Actions ─────────────────────────────────────────────────────────
  const create = useCallback(
    (params: PullJobParams) => {
      setError(null);
      setIsTimedOut(false);
      createMut.mutate(params);
    },
    [createMut],
  );

  const cancel = useCallback(() => {
    if (activeJobId) removeMut.mutate(activeJobId);
    setActiveJobId(null);
    setError(null);
    setIsTimedOut(false);
    jobStatusRef.current = null;
    createdAtRef.current = null;
    stopElapsedTimer();
  }, [activeJobId, removeMut, stopElapsedTimer]);

  const clear = useCallback(() => {
    setActiveJobId(null);
    setError(null);
    setIsTimedOut(false);
    jobStatusRef.current = null;
    createdAtRef.current = null;
    stopElapsedTimer();
  }, [stopElapsedTimer]);

  return {
    activeJobId,
    job,
    isPolling,
    isCreating: createMut.isPending,
    manifest,
    error: error ?? jobError,
    progress,
    elapsedMs,
    isTimedOut,
    create,
    cancel,
    clear,
    jobs: jobsQuery.data ?? [],
    jobsLoading: jobsQuery.isLoading,
  };
}
